// =============================================================================
// Despesa — a "Visão Financeiro" do protótipo
//
// O AF-01 §10 dá a correspondência: a aba "Despesa" do protótipo É a Visão
// Financeiro do PRD, com status "equivalente confirmado". O nome do arquivo
// segue o do PRD; o rótulo na aba segue o do protótipo, porque é o que o
// Financeiro fala.
//
// Os blocos por categoria entraram em 27/10, a pedido do Enzo, "iguais aos do
// Painel do Gestor", somando todos os centros de custo. Até então a tabela por
// categoria morava só na grade do Orçado vs Realizado, para duas cópias não
// divergirem. Elas não divergem porque partem da MESMA consulta da grade
// (v_orcado_realizado_mes, a mesma chave de cache) — e não das linhas
// orçamentárias, como o Painel: o motor guarda o casamento de um lançamento
// rateado numa linha só, e pela coordenada cada CC recebe a sua parte.
//
// A outra metade do critério de aceite: "o Financeiro cadastra e acompanha o
// orçamento de forma consolidada" (AF-01 §13).
//
// É a mesma família de views do Painel do Gestor, sem o recorte de um CC. Quem
// separa as duas visões é a RLS, não esta tela: se um gestor abrir esta rota,
// recebe os CCs dele e mais nada.
// =============================================================================

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { buscarOrcadoRealizado, buscarVisaoFinanceiro, type CentroCustoMes } from "../lib/api.ts";
import { consolidarPorCategoria } from "../lib/categoriasConsolidadas.ts";
import {
  BarrasCategorias,
  DesviosCategorias,
  HeatmapCategoriaMes,
  SaldoCategorias,
  TabelaCategoriasConsolidada,
  type AlvoLancamentos,
} from "../componentes/BlocosCategoria.tsx";
import { ModalLancamentos } from "./ModalLancamentos.tsx";
import {
  competenciaAnterior,
  competenciaLonga,
  mesCurto,
  moeda,
  moedaCompacta,
  percentual,
  type Tom,
} from "../lib/formato.ts";
import {
  COR,
  BarraFiltros,
  CabecalhoGrafico,
  Campo,
  Kpi,
  LegendaEstados,
  LegendaGrafico,
  Painel,
  TooltipRico,
  Variacao,
  Vazio,
} from "../componentes/ui.tsx";
import { RodapeLinhagem } from "../componentes/RodapeLinhagem.tsx";
import { FiltroAno, FiltroMes, resumoPeriodo } from "../componentes/FiltroPeriodo.tsx";
import { BlocoAlertas } from "../componentes/BlocoAlertas.tsx";

export function VisaoFinanceiro({ periodo, onMudarPeriodo }: {
  periodo: string[];
  onMudarPeriodo: (p: string[]) => void;
}) {
  // A competência "corrente" é a última do período: é dela que saem o exercício
  // da série anual e o texto do cabeçalho. Com um mês só — o caso de sempre —
  // ela é o próprio mês.
  const [ordemDesvio, setOrdemDesvio] = useState<"desvio" | "nome">("desvio");
  const competencia = periodo[periodo.length - 1]!;
  const varios = periodo.length > 1;

  const q = useQuery({
    queryKey: ["visao-financeiro", periodo],
    queryFn: () => buscarVisaoFinanceiro(periodo),
  });

  // Os blocos por categoria: a consulta da grade do Orçado vs Realizado, com a
  // mesma chave de cache, somada por categoria nos meses do período.
  const ano = Number(competencia.slice(0, 4));
  const qCategorias = useQuery({
    queryKey: ["orcado-realizado", ano, "despesa"],
    queryFn: () => buscarOrcadoRealizado(ano, "despesa"),
  });
  const categorias = useMemo(
    () => consolidarPorCategoria(qCategorias.data?.linhas ?? [], periodo),
    [qCategorias.data, periodo],
  );
  const [detalhando, setDetalhando] = useState<AlvoLancamentos | null>(null);

  const d = q.data;

  // Série consolidada do ano: soma dos CCs por competência. Soma de subtotais
  // que a view já produziu — não recálculo. Mesmo critério da rota da API.
  const serieConsolidada = useMemo(() => {
    if (!d) return [];
    const porMes = new Map<string, { orcado: bigint; realizado: bigint; comprometido: bigint }>();
    for (const s of d.serie_ano) {
      const atual = porMes.get(s.competencia) ?? { orcado: 0n, realizado: 0n, comprometido: 0n };
      porMes.set(s.competencia, {
        orcado: atual.orcado + BigInt(s.orcado_centavos),
        realizado: atual.realizado + BigInt(s.realizado_centavos),
        comprometido: atual.comprometido + BigInt(s.comprometido_centavos),
      });
    }
    const meses = [...porMes.entries()].sort(([a], [b]) => a.localeCompare(b));

    // ⚠️ Forecast por RUN-RATE, como o protótipo: a média dos três últimos
    // meses fechados, repetida para o que falta do ano. O comprometido, que
    // estava aqui antes, é outra coisa — é o que já está registrado e não
    // venceu, e nos meses distantes ele é quase zero, o que fazia a linha
    // desabar e parecer que a empresa pararia de gastar em novembro.
    const fechados = meses.filter(([comp]) => comp < competencia).slice(-3);
    const ritmo =
      fechados.length === 0
        ? 0
        : fechados.reduce((t, [, v]) => t + Number(v.realizado), 0) / fechados.length / 100;

    return meses.map(([comp, v]) => {
      const passado = comp <= competencia;
      return {
        mes: mesCurto(comp),
        orcado: Number(v.orcado) / 100,
        realizado: passado ? Number(v.realizado) / 100 : null,
        // A linha começa NO último mês realizado — sem isso ela nasce solta no
        // meio do gráfico, sem ligação com o que veio antes.
        projetado: comp < competencia ? null : comp === competencia ? Number(v.realizado) / 100 : ritmo,
      };
    });
  }, [d, competencia]);

  /**
   * Agregados do exercício — o que os KPIs "no ano" e a composição precisam.
   *
   * Soma de SUBTOTAIS que a view já produziu, nunca recálculo a partir de
   * lançamento: é o mesmo princípio do único `sum()` da API
   * (docs/CONTRATO_DADOS.md §2). Somar por centro de custo é seguro mesmo com
   * rateio, porque cada CC já carrega só a sua fração.
   */
  const agregados = useMemo(() => {
    const zero = () => ({
      orcado: 0n,
      realizado: 0n,
      comprometido: 0n,
      disponivel: 0n,
      excedentes: 0,
    });
    const ano = zero();
    const ytd = zero();
    const anterior = zero();
    const porCc = new Map<
      string,
      { codigo: string; nome: string; realizado: bigint; orcado: bigint }
    >();

    const mesAnterior = competenciaAnterior(competencia);
    // ⚠️ Janeiro não tem mês anterior DENTRO do exercício, e `serie_ano` só
    // traz o exercício. Comparar com zero diria "subiu infinito"; o certo é
    // dizer que não há com o que comparar.
    const temAnterior = mesAnterior.slice(0, 4) === competencia.slice(0, 4);

    for (const s of d?.serie_ano ?? []) {
      const alvos = [ano];
      if (s.competencia <= competencia) alvos.push(ytd);
      if (temAnterior && s.competencia === mesAnterior) alvos.push(anterior);

      for (const t of alvos) {
        t.orcado += BigInt(s.orcado_centavos);
        t.realizado += BigInt(s.realizado_centavos);
        t.comprometido += BigInt(s.comprometido_centavos);
        t.disponivel += BigInt(s.disponivel_centavos);
        t.excedentes += s.qtd_linhas_excedentes;
      }

      if (s.competencia <= competencia) {
        const atual = porCc.get(s.centro_custo_id) ?? {
          codigo: s.codigo,
          nome: s.nome,
          realizado: 0n,
          orcado: 0n,
        };
        atual.realizado += BigInt(s.realizado_centavos);
        atual.orcado += BigInt(s.orcado_centavos);
        porCc.set(s.centro_custo_id, atual);
      }
    }

    // Quantos centros de custo estouraram no mês anterior — a comparação que o
    // protótipo põe no sexto cartão. Sem ela, "3 de 7" não diz se está piorando.
    let ccsAcimaAnterior: number | null = null;
    if (temAnterior) {
      const doMesAnterior = (d?.serie_ano ?? []).filter((x) => x.competencia === mesAnterior);
      ccsAcimaAnterior = doMesAnterior.filter(
        (x) => BigInt(x.orcado_centavos) > 0n && BigInt(x.realizado_centavos) > BigInt(x.orcado_centavos),
      ).length;
    }

    const centrosAno = [...porCc.values()].sort((x, y) =>
      y.realizado > x.realizado ? 1 : y.realizado < x.realizado ? -1 : 0,
    );

    return {
      ano,
      ytd,
      anterior: temAnterior ? anterior : null,
      ccsAcimaAnterior,
      qtdCentrosAnterior: temAnterior
        ? (d?.serie_ano ?? []).filter((x) => x.competencia === mesAnterior).length
        : 0,
      centrosAno,
      ccsAcimaNoAno: centrosAno.filter((c) => c.orcado > 0n && c.realizado > c.orcado).length,
    };
  }, [d, competencia]);

  if (q.isPending) return <Esqueleto />;

  if (q.isError) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Vazio
          titulo="Não foi possível carregar a visão consolidada"
          dica={(q.error as { message?: string })?.message ?? "Tente recarregar a página."}
        />
      </div>
    );
  }

  if (!d) return null;

  const c = d.consolidado;
  const pct =
    BigInt(c.orcado_centavos) === 0n
      ? 0
      : Number((BigInt(c.realizado_centavos) * 10000n) / BigInt(c.orcado_centavos)) / 100;
  const tom: Tom = pct > 100 ? "risco" : pct >= 90 ? "atencao" : "ok";
  const disponivelNegativo = BigInt(c.disponivel_centavos) < 0n;

  // ⚠️ Quanto do realizado do mês NÃO está nesta tela.
  //
  // Todas as views desta página agregam por centro de custo; lançamento sem CC
  // vinculado não entra em nenhuma. Não é defeito — é a fila de exceções
  // esperando alocação —, mas quem lê o Realizado como total conclui errado.
  // Medido em produção: entre 10% e 22% do valor, mês a mês.
  //
  // Para o gestor isto é sempre zero: pela RLS ele só enxerga lançamento dos
  // CCs dele, e o que não tem CC não é de ninguém. A ressalva é do consolidado,
  // que é onde a soma engana.
  // ⚠️ Pago, e não `valor_centavos`. O KPI "Realizado" mostra o PAGO — medir a
  // fatia pelo valor total compararia o realizado com o total contratado, que é
  // outra grandeza. Correção do Enzo ao criar a view (migration 033).
  // ⚠️ `cobertura` é nula em competência sem lançamento nenhum: a view agrupa
  // por competência e mês vazio não gera linha.
  const { ano: noAno, ytd, anterior } = agregados;

  // Ritmo esperado: em setembro, 9 dos 12 meses passaram, então 75% do orçado
  // anual é o consumo "no ritmo". É a régua que falta ao percentual sozinho —
  // 70% de execução é ótimo em dezembro e alarmante em março.
  const ritmoEsperado = (Number(competencia.slice(5, 7)) / 12) * 100;

  const variacaoVsOrcado = BigInt(c.orcado_centavos) > 0n ? pct - 100 : null;
  const variacaoVsMesAnterior =
    anterior && anterior.realizado > 0n
      ? (Number(BigInt(c.realizado_centavos) - anterior.realizado) / Number(anterior.realizado)) * 100
      : null;

  const ccsAcimaNoMes = d.centros.filter(
    (x) => BigInt(x.orcado_centavos) > 0n && BigInt(x.realizado_centavos) > BigInt(x.orcado_centavos),
  ).length;

  const fatia = (parte: bigint, total: bigint) =>
    total === 0n ? "—" : percentual(Number((parte * 10000n) / total) / 100);

  const totalMes = BigInt(d.cobertura?.pago_centavos ?? 0);
  const descoberto = BigInt(d.cobertura?.pago_sem_centro_custo ?? 0);
  const pctNaTela =
    totalMes === 0n ? 100 : Number(((totalMes - descoberto) * 10000n) / totalMes) / 100;

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-5">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Visão consolidada
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Orçamento de todos os centros de custo
        </h1>
        <p className="mt-1 text-[14px] text-tinta-fraca">
            {competenciaLonga(competencia)} · {c.qtd_centros}{" "}
            {c.qtd_centros === 1 ? "centro de custo" : "centros de custo"}
            {d.excecoes_abertas > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-atencao-tinta">
                  {d.excecoes_abertas} {d.excecoes_abertas === 1 ? "pendência" : "pendências"} na
                  Central Operacional
                </span>
              </>
            )}
        </p>
      </header>

      {/* A barra de filtros do protótipo: o controle que muda a tela inteira,
          com a legenda das cores ao lado. */}
      <BarraFiltros legenda={<LegendaEstados />} className="mb-6">
        <Campo rotulo="Mês">
          <FiltroMes periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>
        <Campo rotulo="Ano">
          <FiltroAno periodo={periodo} onMudar={onMudarPeriodo} />
        </Campo>
      </BarraFiltros>

      {/* ── KPIs ─────────────────────────────────────────────────────────
          Os seis cartões do protótipo, na ordem dele. O que era um alternador
          Mês / Acumulado no ano saiu: o filtro de período faz as duas coisas e
          mais — "acumulado no ano" é marcar Jan a Set, e agora dá para pedir um
          trimestre, que o alternador não permitia. Dois controles para a mesma
          pergunta ensinam a duvidar dos dois. */}
      <p className="mb-3 text-[12.5px] text-tinta-fraca">
        {varios
          ? `Indicadores somados de ${periodo.length} competências — ${resumoPeriodo(periodo)}.`
          : `Indicadores de ${competenciaLonga(competencia)}.`}
      </p>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-3.5">
        {/* Os rótulos são os do protótipo. ⚠️ Sem o "(YTD)" que ele usa: lá o
            filtro é de um mês e os cartões mostram sempre o acumulado do ano;
            aqui o período é escolhido, e chamar de YTD o que pode ser um mês
            avulso seria rótulo mentindo sobre o número. A janela aparece na
            linha de apoio. */}
        <Kpi
          rotulo="Despesa orçada"
          valor={moeda(c.orcado_centavos)}
          apoio={varios ? resumoPeriodo(periodo) : competenciaLonga(competencia)}
          comparacoes={[
            { rotulo: "Ref. anual", valor: moedaCompacta(String(noAno.orcado)) },
            { rotulo: "Acumulado no ano", valor: moedaCompacta(String(ytd.orcado)) },
          ]}
        />
        <Kpi
          rotulo="Despesa realizada"
          valor={moeda(c.realizado_centavos)}
          tom={tom}
          apoio={
            descoberto > 0n
              ? `${percentual(pctNaTela)} do realizado do período`
              : "todo o realizado do período"
          }
          dica="Soma do que fechou a competência, agregada por centro de custo. Lançamento ainda sem centro de custo vinculado não entra nesta soma — quando houver, o apoio abaixo do número diz qual fatia está representada aqui."
          comparacoes={[
            { rotulo: "vs orçado", valor: <Variacao pct={variacaoVsOrcado} /> },
            {
              rotulo: varios ? "Realizado no ano" : "vs mês anterior",
              valor: varios ? (
                moedaCompacta(String(ytd.realizado))
              ) : (
                <Variacao pct={variacaoVsMesAnterior} />
              ),
            },
          ]}
        />
        <Kpi
          rotulo="Despesa comprometida"
          valor={moeda(c.comprometido_centavos)}
          dica="Lançamento registrado no Conta Azul e ainda não pago, vencido ou não. Compromisso que ainda não foi lançado lá não entra neste número."
          comparacoes={[
            {
              rotulo: "% do orçado",
              valor: fatia(BigInt(c.comprometido_centavos), BigInt(c.orcado_centavos)),
            },
            {
              rotulo: "Mês anterior",
              valor: anterior ? moedaCompacta(String(anterior.comprometido)) : "—",
            },
          ]}
        />
        <Kpi
          rotulo="Saldo orçamentário disponível"
          valor={moeda(c.disponivel_centavos)}
          tom={disponivelNegativo ? "risco" : undefined}
          destaque
          comparacoes={[
            {
              rotulo: "% do orçado",
              valor: fatia(BigInt(c.disponivel_centavos), BigInt(c.orcado_centavos)),
            },
            {
              rotulo: "Mês anterior",
              valor: anterior ? moedaCompacta(String(anterior.disponivel)) : "—",
            },
          ]}
        />
        <Kpi
          rotulo="% de execução do orçamento"
          valor={percentual(pct)}
          tom={tom}
          dica="Realizado sobre orçado, no período selecionado. O ritmo esperado é a fração do ano que já passou — é ele que diz se o percentual é alto ou apenas tardio."
          comparacoes={[
            { rotulo: "Ritmo esperado no ano", valor: percentual(ritmoEsperado) },
            {
              rotulo: "Realizado / orçado",
              valor: `${moedaCompacta(c.realizado_centavos)} / ${moedaCompacta(c.orcado_centavos)}`,
            },
          ]}
        />
        <Kpi
          rotulo="Centros de custo acima do orçamento"
          valor={`${ccsAcimaNoMes} de ${c.qtd_centros}`}
          tom={ccsAcimaNoMes > 2 ? "risco" : ccsAcimaNoMes > 0 ? "atencao" : "ok"}
          apoio={varios ? "no período selecionado" : "no mês selecionado"}
          dica="Centro de custo cujo realizado passou do orçado. O AF-01 §10 registra este indicador como visto no protótipo, sem confirmação textual no PRD."
          comparacoes={[
            {
              rotulo: "Mês anterior",
              valor:
                agregados.ccsAcimaAnterior === null
                  ? "—"
                  : `${agregados.ccsAcimaAnterior} de ${agregados.qtdCentrosAnterior}`,
            },
            { rotulo: "Linhas excedentes", valor: String(c.qtd_linhas_excedentes) },
          ]}
        />
      </div>

      {descoberto > 0n && (
        <p className="mt-3 rounded-lg border border-atencao-linha bg-atencao-bg px-4 py-2.5 text-[12.5px] leading-snug text-atencao-tinta">
          <span className="font-semibold">Leia o Realizado com esta ressalva:</span>{" "}
          {d.cobertura?.sem_centro_custo} dos {d.cobertura?.lancamentos} lançamentos da competência
          ainda não têm centro de custo vinculado, e por isso ficam fora de todos os números e
          gráficos desta tela — que são agregados por centro de custo. O que está representado aqui
          é <span className="num font-semibold">{percentual(pctNaTela)}</span> do realizado do
          mês. A
          alocação é feita na Central Operacional; conforme ela avança, este aviso some sozinho.
        </p>
      )}

      {/* ── mês a mês ────────────────────────────────────────────────────── */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Orçado, realizado e forecast por mês"
          dica="Despesa total · colunas para orçado e realizado, linha para o forecast (run-rate dos últimos 3 meses). A linha parte do último mês realizado, para as duas leituras se encontrarem em vez de a projeção nascer solta."
        />
        {serieConsolidada.length === 0 ? (
          <Vazio titulo="Sem orçamento cadastrado para este exercício" />
        ) : (
          <>
            <div className="h-[280px] w-full">
            <ResponsiveContainer>
              <ComposedChart data={serieConsolidada} margin={{ top: 8, right: 8, left: 8 }}>
                <CartesianGrid stroke="#eef1f6" vertical={false} />
                <XAxis
                  dataKey="mes"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "#6b7789" }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={68}
                  tick={{ fontSize: 11, fill: "#6b7789" }}
                  tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
                />
                <Tooltip
                  cursor={{ fill: "rgba(16,40,80,0.04)" }}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <TooltipRico
                        titulo={String(label)}
                        itens={payload
                          .filter((p) => p.value !== null && p.value !== undefined)
                          .map((p) => ({
                            rotulo:
                              p.dataKey === "orcado"
                                ? "Orçado"
                                : p.dataKey === "realizado"
                                  ? "Realizado"
                                  : "Forecast",
                            cor: p.color,
                            valor: (p.value as number).toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }),
                          }))}
                      />
                    ) : null
                  }
                />
                <Bar dataKey="orcado" fill={COR.orcado} radius={[2, 2, 0, 0]} maxBarSize={28} />
                <Bar dataKey="realizado" fill={COR.realizado} radius={[2, 2, 0, 0]} maxBarSize={28} />
                <Line
                  type="monotone"
                  dataKey="projetado"
                  stroke={COR.forecast}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={{ r: 3 }}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
            </div>

            {/* ⚠️ FORA do `div` de altura fixa. Dentro, o `ResponsiveContainer`
                ocupa os 100% e empurra a legenda para fora do cartão — foi
                exatamente assim que ela sumiu no "Meu orçamento". */}
            <LegendaGrafico
              series={[
                ["Orçado", COR.orcado],
                ["Realizado", COR.realizado],
                ["Forecast", COR.forecast],
              ]}
            />
          </>
        )}
      </Painel>

      {/* ── alertas do AF-01 (053), de todos os CCs ──────────────────────── */}
      <BlocoAlertas periodo={periodo} mostrarCentro />

      {/* ── composição e desvio ──────────────────────────────────────────
          O `row-2` do protótipo: duas colunas, os dois gráficos de barras
          horizontais. ⚠️ Saiu daqui o painel "Centros de custo acima do
          orçamento": no protótipo isso é um CARTÃO de indicador, e ele já está
          entre os seis lá em cima — a lista repetia o número em outro formato. */}
      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Painel>
          <CabecalhoGrafico
            titulo="Composição das despesas"
            dica="Por centro de custo, realizado acumulado no ano até o mês selecionado, do maior para o menor. Responde onde o dinheiro do exercício foi parar — outra pergunta do que o desvio do mês."
          />
          <ComposicaoNoAno centros={agregados.centrosAno} total={ytd.realizado} />
        </Painel>

        <Painel>
          <CabecalhoGrafico
            titulo="Desvio por centro de custo"
            dica="Mês selecionado · realizado menos orçado. Barra à direita do zero é estouro; à esquerda, sobra."
            acessorio={
              <select
                aria-label="Ordenação do desvio"
                value={ordemDesvio}
                onChange={(e) => setOrdemDesvio(e.target.value as "desvio" | "nome")}
                className="rounded-lg border border-linha bg-superficie px-2.5 py-1 text-[12px]"
              >
                <option value="desvio">Ordenar por desvio</option>
                <option value="nome">Ordenar por nome</option>
              </select>
            }
          />
          <DesvioPorCc centros={d.centros} ordem={ordemDesvio} />
        </Painel>
      </div>

      {/* ── matriz de calor ──────────────────────────────────────────────
          ⚠️ Era centro de custo × CATEGORIA, na competência. O protótipo faz
          centro de custo × MÊS, e a diferença é o que a matriz responde: por
          categoria num mês só, ela repete o que a tabela já diz; por mês, ela
          mostra o PADRÃO — quem estoura sempre e quem estourou uma vez. */}
      <Painel className="mt-6">
        <CabecalhoGrafico
          titulo="Matriz de calor — execução por centro de custo"
          dica="Percentual do orçado consumido em cada mês do exercício, por centro de custo."
        />
        <MatrizCalorMeses serie={d.serie_ano} ate={competencia} />
      </Painel>

      {/* ── por categoria, todos os centros de custo (27/10) ─────────────────
          Os blocos do Painel do Gestor, na mesma ordem, somando as áreas. Com
          ~35 categorias, os gráficos mostram as 10 maiores; as tabelas, todas. */}
      {qCategorias.isError ? (
        <Painel className="mt-6">
          <Vazio titulo="Não foi possível carregar a despesa por categoria" dica={(qCategorias.error as { message?: string })?.message} />
        </Painel>
      ) : qCategorias.isPending ? (
        <div className="mt-6 h-64 animate-pulse rounded-lg border border-linha bg-superficie" />
      ) : (
        <>
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <Painel>
              <CabecalhoGrafico
                titulo="Gastos por categoria"
                dica="As 10 categorias que mais gastaram no período, todos os centros de custo somados. A barra fica vermelha quando passa do orçado da categoria, e amarela quando a categoria gastou sem orçamento."
              />
              <BarrasCategorias itens={categorias} varios={varios} />
            </Painel>
            <Painel>
              <CabecalhoGrafico
                titulo="Saldo por categoria"
                dica="As 10 categorias com menor saldo no período: orçado menos realizado e comprometido. Barra à esquerda do zero é categoria que já passou do teto."
              />
              <SaldoCategorias itens={categorias} />
            </Painel>
          </div>

          <Painel className="mt-6">
            <CabecalhoGrafico
              titulo="Orçado × realizado por categoria"
              dica="Todas as categorias, todos os centros de custo somados. Clique numa categoria para ver cada centro de custo dela; com um mês selecionado, cada um tem o caminho até os lançamentos. A mesma conta da grade do Orçado vs Realizado, agrupada por categoria."
            />
            <TabelaCategoriasConsolidada itens={categorias} competencia={competencia} varios={varios} onVer={setDetalhando} />
          </Painel>

          <Painel className="mt-6">
            <CabecalhoGrafico
              titulo="Heatmap de consumo por categoria"
              dica="Percentual do orçado consumido por categoria em CADA mês do exercício, todos os centros de custo somados. É onde se vê o padrão: categoria que estoura sempre é outro problema do que categoria que estourou uma vez."
            />
            <HeatmapCategoriaMes exercicio={ano} centroCustoId={null} ate={competencia} />
          </Painel>

          <Painel className="mt-6">
            <CabecalhoGrafico
              titulo="Principais desvios por categoria"
              dica="As 10 categorias mais acima do orçado no período, todos os centros de custo somados, com quais centros de custo passaram do próprio orçado. Gasto sem orçamento conta como desvio."
            />
            <DesviosCategorias itens={categorias} competencia={competencia} varios={varios} onVer={setDetalhando} />
          </Painel>
        </>
      )}

      <p className="mt-4 text-[12px] leading-relaxed text-tinta-fraca">
        A grade mês a mês por centro de custo e categoria, com quem recebeu em cada uma, está na aba{" "}
        <strong className="font-semibold text-tinta-media">Orçado vs Realizado</strong>.
      </p>

      {detalhando && <ModalLancamentos linha={detalhando} onFechar={() => setDetalhando(null)} />}

      {/* ── rodapé de linhagem (§5.1) ────────────────────────────────────── */}
      <RodapeLinhagem />
    </div>
  );
}

// ── blocos ──────────────────────────────────────────────────────────────────

function Esqueleto() {
  return (
    <div className="pagina animate-pulse py-8">
      <div className="h-8 w-96 rounded-lg bg-superficie-2" />
      <div className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-3.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-[88px] rounded-lg border border-linha bg-superficie" />
        ))}
      </div>
      <div className="mt-4 h-72 rounded-lg border border-linha bg-superficie" />
    </div>
  );
}

function DesvioPorCc({
  centros,
  ordem,
}: {
  centros: CentroCustoMes[];
  ordem: "desvio" | "nome";
}) {
  if (centros.length === 0) return <Vazio titulo="Nenhum centro de custo nesta competência" />;

  const dados = centros
    .map((c) => ({
      nome: c.codigo,
      nomeCompleto: c.nome,
      desvio: (Number(BigInt(c.realizado_centavos) - BigInt(c.orcado_centavos)) / 100),
    }))
    .sort((a, b) => (ordem === "nome" ? a.nome.localeCompare(b.nome) : b.desvio - a.desvio));

  return (
    <div style={{ height: Math.max(dados.length * 34, 120) }}>
      <ResponsiveContainer>
        <BarChart data={dados} layout="vertical" margin={{ left: 4, right: 12, top: 4, bottom: 4 }}>
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10.5, fill: "#6b7789" }}
            tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))}
          />
          <YAxis
            type="category"
            dataKey="nome"
            width={44}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11.5, fill: "#48566e" }}
          />
          {/* O zero é a referência que dá sentido ao sinal da barra. */}
          <ReferenceLine x={0} stroke="#c3cbd9" />
          <Tooltip
            cursor={{ fill: "rgba(16,40,80,0.04)" }}
            content={({ active, payload }) => {
              const p = payload?.[0]?.payload as
                | { nomeCompleto: string; desvio: number }
                | undefined;
              return active && p ? (
                <TooltipRico
                  titulo={p.nomeCompleto}
                  itens={[
                    {
                      rotulo: p.desvio >= 0 ? "Acima do orçado" : "Abaixo do orçado",
                      valor: Math.abs(p.desvio).toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }),
                    },
                  ]}
                />
              ) : null;
            }}
          />
          <Bar dataKey="desvio" radius={2} maxBarSize={18}>
            {dados.map((d, i) => (
              <Cell key={i} fill={d.desvio >= 0 ? COR.negativo : COR.positivo} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Onde o realizado do exercício se concentra.
 *
 * Barras horizontais, e não rosca: são 7 a 8 centros de custo com nomes longos,
 * e rosca com legenda ao lado obriga o olho a ir e voltar para ler cada fatia.
 * Em barra, o nome fica encostado no próprio valor.
 */
function ComposicaoNoAno({
  centros,
  total,
}: {
  centros: { codigo: string; nome: string; realizado: bigint; orcado: bigint }[];
  total: bigint;
}) {
  const comGasto = centros.filter((c) => c.realizado > 0n);
  if (comGasto.length === 0 || total === 0n) {
    return <Vazio titulo="Ainda não há realizado acumulado neste exercício" />;
  }

  const maior = comGasto[0]!.realizado;

  return (
    <ul className="thin-scroll max-h-[300px] overflow-y-auto pr-1">
      {comGasto.map((c) => {
        const fatia = Number((c.realizado * 10000n) / total) / 100;
        // A barra é proporcional ao MAIOR, não ao total: com um centro de custo
        // respondendo por metade da despesa, as barras dos outros ficariam
        // indistinguíveis umas das outras. O percentual do total vai ao lado.
        const largura = Number((c.realizado * 1000n) / maior) / 10;
        const estourou = c.orcado > 0n && c.realizado > c.orcado;
        return (
          <li key={c.codigo} className="border-t border-linha py-2 first:border-t-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-[13px] font-medium" title={c.nome}>
                <span className="num text-tinta-fraca">{c.codigo}</span> {c.nome}
              </span>
              <span className="num shrink-0 text-[12.5px] font-bold">{percentual(fatia)}</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-superficie-2">
              <div
                className={`h-full rounded-full ${estourou ? "bg-risco-tinta" : "bg-azul-medio"}`}
                style={{ width: `${largura}%` }}
              />
            </div>
            <p className="num mt-1 text-[11.5px] text-tinta-fraca">
              {moeda(String(c.realizado))}
              {c.orcado > 0n && <> de {moeda(String(c.orcado))} orçados no ano</>}
            </p>
          </li>
        );
      })}
    </ul>
  );
}



/**
 * Matriz de calor — execução por centro de custo, mês a mês.
 *
 * É a do protótipo: linha por centro de custo, coluna por mês do exercício,
 * célula com o percentual do orçado consumido naquele mês. A leitura que ela
 * dá e a versão por categoria não dava é o PADRÃO — quem estoura todo mês é um
 * problema de orçamento; quem estourou uma vez é um evento.
 *
 * Vem de `serie_ano`, que a resposta já traz para o gráfico mensal: nenhuma
 * requisição a mais.
 */
function MatrizCalorMeses({ serie, ate }: { serie: CentroCustoMes[]; ate: string }) {
  const ateMes = Number(ate.slice(5, 7));
  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"]
    .slice(0, ateMes);

  const linhas = useMemo(() => {
    const mapa = new Map<string, { codigo: string; nome: string; pct: (number | null)[] }>();
    for (const x of serie) {
      const i = Number(x.competencia.slice(5, 7)) - 1;
      if (i >= ateMes) continue;
      const atual =
        mapa.get(x.centro_custo_id) ??
        {
          codigo: x.codigo,
          nome: x.nome,
          pct: Array.from({ length: ateMes }, () => null as number | null),
        };
      // Sem orçado no mês, o percentual não existe — e mostrar 0% diria
      // "consumiu nada do que tinha", que é diferente de "não tinha".
      atual.pct[i] =
        BigInt(x.orcado_centavos) === 0n
          ? null
          : Number((BigInt(x.realizado_centavos) * 10000n) / BigInt(x.orcado_centavos)) / 100;
      mapa.set(x.centro_custo_id, atual);
    }
    return [...mapa.values()].sort((a, b) => a.codigo.localeCompare(b.codigo));
  }, [serie, ateMes]);

  if (linhas.length === 0) {
    return <Vazio titulo="Sem orçamento cadastrado para este exercício" />;
  }

  const cor = (p: number | null) => {
    if (p === null) return "bg-superficie-2 text-tinta-fraca";
    if (p > 100) return "bg-risco-bg text-risco-tinta";
    if (p >= 70) return "bg-atencao-bg text-atencao-tinta";
    return "bg-ok-bg text-ok-tinta";
  };

  return (
    <>
      <div className="thin-scroll overflow-x-auto">
        <table className="w-full min-w-[680px] text-[12px]">
          <thead>
            <tr className="text-[11px] text-tinta-fraca uppercase">
              <th className="px-2 py-1.5 text-left font-semibold">Centro de custo</th>
              {meses.map((m) => (
                <th key={m} className="px-1 py-1.5 text-center font-semibold">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.codigo}>
                <td className="px-2 py-1 text-[12.5px] whitespace-nowrap">
                  <span className="num text-tinta-fraca">{l.codigo}</span> {l.nome}
                </td>
                {l.pct.map((pp, i) => (
                  <td key={i} className="p-0.5">
                    <span
                      title={pp === null ? "sem orçamento no mês" : `${percentual(pp)} do orçado`}
                      className={`block rounded-md px-1 py-2 text-center text-[11px] font-semibold ${cor(pp)}`}
                    >
                      {pp === null ? "—" : `${Math.round(pp)}%`}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-[11.5px] text-tinta-fraca">
        <span className="rounded-md bg-ok-bg px-2 py-0.5 text-ok-tinta">até 70%</span>
        <span className="rounded-md bg-atencao-bg px-2 py-0.5 text-atencao-tinta">70–100%</span>
        <span className="rounded-md bg-risco-bg px-2 py-0.5 text-risco-tinta">acima de 100%</span>
      </div>
    </>
  );
}
